// v3/layouts/MobileLayout.tsx — Mobile hamburger nav
// Navy background sheet matching sidebar.
// Zero imports from v1/v2.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../design-system/primitives/sheet';
import { Separator } from '../design-system/primitives/separator';
import { cn } from '../design-system/utils';
import { NAVIGATION_ITEMS } from '../config/navigation.config';
import { usePermission } from '../shared/hooks/usePermission';
import { useAuth } from '../shared/hooks/useAuth';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

function getIcon(name: string): LucideIcon {
  return (Icons as unknown as Record<string, LucideIcon>)[name] ?? Icons.Circle;
}

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { canAccessFeature } = usePermission();
  const { user, logout } = useAuth();
  const items = NAVIGATION_ITEMS.filter((i) => canAccessFeature(i.featureId));

  const go = (path: string) => { setOpen(false); navigate(path); };

  return (
    <>
      <button onClick={() => setOpen(true)} className="p-1.5 rounded hover:bg-slate-100 transition-colors">
        <Menu className="h-5 w-5 text-slate-600" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[75vw] max-w-[260px] p-0 border-0 text-white" style={{ background: '#0f172a' }}>
          <SheetHeader className="px-4 py-3">
            <SheetTitle>
              <img src="/assets/logo2.jpg" alt="Pargro" className="h-9 rounded-lg" />
            </SheetTitle>
          </SheetHeader>

          <Separator className="bg-white/10" />

          <nav className="flex-1 py-1.5 px-1.5">
            {items.map((item) => {
              const Icon = getIcon(item.icon);
              return (
                <button
                  key={item.path}
                  onClick={() => go(item.path)}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] font-medium text-white/60 hover:bg-white/10 hover:text-white transition-colors mb-0.5"
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <Separator className="bg-white/10" />

          <div className="p-3">
            {user && (
              <div className="mb-2 text-sm">
                <p className="font-medium text-white">{user.userName}</p>
                <p className="text-[11px] text-white/50">
                  {user.roleAssigned === false ? 'No role assigned' : user.role?.replace(/_/g, ' ')}
                </p>
              </div>
            )}
            <button
              onClick={() => { setOpen(false); logout(); }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[13px] font-medium text-red-400 hover:bg-white/10 transition-colors"
            >
              <Icons.LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
